import { useCreateObject } from './useCreateObject';
import { useObject } from 'utils/api/useObject';
import { useEditObjectMutation } from '../../api/mutations/generated/EditObject';
import { CreateNewObjectInput } from 'generated/graphql';
import { useParams } from 'react-router';
import { useCallback } from 'react';
import { errorToast, successToast } from 'utils/helpers/notify';
import { errorHandler } from 'utils/helpers/errorHandler';
import { GetObjectDocument } from 'pages/facility/api/queries/generated/GetObject';

interface EditObjectSettingsInput {
  checkInMeasurement?: number;
  checkOutMeasurement?: number;
  isDisplayBonusesWorkers?: boolean;
  isDisplayIncomeBeforeModeration?: boolean;
  isDisplayPaymentsWorkers?: boolean;
  isDefaultAllowWorkWithoutNaimix?: boolean;
  cityId?: string | null;
  naimixLegalEntityId?: string | null;
  metroStationIds?: string[] | null;
  address?: {
    addressText: string;
    lat: number;
    lon: number;
  } | null;
  radius?: number | null;
  avatar?: File | null;
}

interface EditObjectInput {
  objectId: string;
  name?: string;
  settings: EditObjectSettingsInput;
}

const mapCreateInputToEditInput = (input: CreateNewObjectInput): EditObjectInput => {
  const { settings, name } = input;
  if (!settings) return { objectId: '', settings: {} };

  const mappedSettings: EditObjectSettingsInput = {
    checkInMeasurement: settings.checkInMeasurement,
    checkOutMeasurement: settings.checkOutMeasurement,
    isDisplayBonusesWorkers: settings.isDisplayBonusesWorkers,
    isDisplayIncomeBeforeModeration: settings.isDisplayIncomeBeforeModeration,
    isDisplayPaymentsWorkers: settings.isDisplayPaymentsWorkers,
    isDefaultAllowWorkWithoutNaimix: settings.isDefaultAllowWorkWithoutNaimix,
  };

  if (settings.cityId) mappedSettings.cityId = settings.cityId;
  if (settings.naimixLegalEntityId) mappedSettings.naimixLegalEntityId = settings.naimixLegalEntityId;
  if (settings.metroStationIds) mappedSettings.metroStationIds = settings.metroStationIds;
  if (settings.address) mappedSettings.address = settings.address;
  if (settings.radius) mappedSettings.radius = settings.radius;
  if (settings.avatar) mappedSettings.avatar = settings.avatar;

  const editInput: EditObjectInput = {
    objectId: '',
    settings: mappedSettings,
  };

  if (name) editInput.name = name;

  return editInput;
};

export const useObjectOperations = () => {
  const { facilityId } = useParams();
  const { createObject, loading: isLoadingCreate } = useCreateObject();
  const { object, loading: isLoading } = useObject();
  const [editObject, { loading: isEditing }] = useEditObjectMutation();

  const handleEditObject = useCallback(
    async (input: CreateNewObjectInput) => {
      if (!facilityId) return;

      const editInput = mapCreateInputToEditInput(input);
      editInput.objectId = facilityId;

      await editObject({
        variables: {
          input: editInput,
        },
        update: (cache, { data }) => {
          if (data?.editObject) {
            cache.writeQuery({
              query: GetObjectDocument,
              variables: { id: facilityId },
              data: {
                object: data.editObject,
              },
            });
          }
        },
      })
        .then(() => {
          successToast('Объект успешно обновлен');
        })
        .catch(e => {
          errorToast(errorHandler(e));
        });
    },
    [facilityId, editObject],
  );

  const handleSubmit = useCallback(
    async (input: CreateNewObjectInput) => {
      if (facilityId) {
        await handleEditObject(input);
      } else {
        await createObject(input);
      }
    },
    [facilityId, handleEditObject, createObject],
  );

  return {
    object,
    isLoading: isLoading || isLoadingCreate || isEditing,
    isLoadingCreate: !facilityId ? isLoadingCreate : isEditing,
    handleSubmit,
    facilityId,
  };
};
